import type { DiagnosedIcd, DiagnosedIcdRelationship } from 'types/result';

export type DiagnosedIcdUiCategory = 'D' | 'DD' | 'DDA';
export type DiagnosedIcdApiCategory = DiagnosedIcdUiCategory;
export type DiagnosedIcdUiCategoryTranslationKey =
  'akutdiagnose' | 'dauerdiagnose' | 'dauerdiagnose_anamnestisch';

export type DiagnosedIcdUiSicherheit =
  'gesichert' | 'ausschluss_von' | 'verdacht_auf' | 'zustand_nach';
export type DiagnosedIcdApiSicherheit = 'G' | 'A' | 'V' | 'Z';

export type DiagnosedIcdUiSicherheitTranslationKey = DiagnosedIcdUiSicherheit;

type CategoryVariant = {
  translationKey: DiagnosedIcdUiCategoryTranslationKey;
  apiValue: DiagnosedIcdApiCategory;
};

type SicherheitVariant = {
  translationKey: DiagnosedIcdUiSicherheitTranslationKey;
  apiValue: DiagnosedIcdApiSicherheit;
  code: 'G' | 'A' | 'V' | 'Z';
};

export const CATEGORY_DEFAULT_LABELS: Record<DiagnosedIcdUiCategoryTranslationKey, string> = {
  akutdiagnose: 'Akutdiagnose',
  dauerdiagnose: 'Dauerdiagnose',
  dauerdiagnose_anamnestisch: 'Dauerdiagnose anamnestisch',
};

export const SICHERHEIT_DEFAULT_LABELS: Record<DiagnosedIcdUiSicherheitTranslationKey, string> = {
  gesichert: 'Gesichert',
  ausschluss_von: 'Ausschluss von',
  verdacht_auf: 'Verdacht auf',
  zustand_nach: 'Zustand nach',
};

const CATEGORY_VARIANTS: Record<DiagnosedIcdUiCategory, CategoryVariant> = {
  D: { translationKey: 'akutdiagnose', apiValue: 'D' },
  DD: { translationKey: 'dauerdiagnose', apiValue: 'DD' },
  DDA: { translationKey: 'dauerdiagnose_anamnestisch', apiValue: 'DDA' },
};

const CATEGORY_RAW_LOOKUP: Record<string, DiagnosedIcdUiCategory> = {
  d: 'D',
  dd: 'DD',
  dda: 'DDA',
  acute: 'D',
  long_term: 'DD',
  akutdiagnose: 'D',
  dauerdiagnose: 'DD',
  'dauerdiagnose anamnestisch': 'DDA',
  dauerdiagnose_anamnestisch: 'DDA',
};

const SICHERHEIT_VARIANTS: Record<DiagnosedIcdUiSicherheit, SicherheitVariant> = {
  gesichert: { translationKey: 'gesichert', apiValue: 'G', code: 'G' },
  ausschluss_von: { translationKey: 'ausschluss_von', apiValue: 'A', code: 'A' },
  verdacht_auf: { translationKey: 'verdacht_auf', apiValue: 'V', code: 'V' },
  zustand_nach: { translationKey: 'zustand_nach', apiValue: 'Z', code: 'Z' },
};

const SICHERHEIT_RAW_LOOKUP: Record<string, DiagnosedIcdUiSicherheit> = {
  g: 'gesichert',
  G: 'gesichert',
  gesichert: 'gesichert',
  a: 'ausschluss_von',
  A: 'ausschluss_von',
  'ausschluss von': 'ausschluss_von',
  'ausschluss-von': 'ausschluss_von',
  ausschluss_von: 'ausschluss_von',
  v: 'verdacht_auf',
  V: 'verdacht_auf',
  verdacht: 'verdacht_auf',
  'verdacht auf': 'verdacht_auf',
  'verdacht-auf': 'verdacht_auf',
  verdacht_auf: 'verdacht_auf',
  z: 'zustand_nach',
  Z: 'zustand_nach',
  'zustand nach': 'zustand_nach',
  'zustand-nach': 'zustand_nach',
  zustand_nach: 'zustand_nach',
};

const CATEGORY_ORDER: DiagnosedIcdUiCategory[] = ['D', 'DD', 'DDA'];
const SICHERHEIT_ORDER: DiagnosedIcdUiSicherheit[] = [
  'gesichert',
  'ausschluss_von',
  'verdacht_auf',
  'zustand_nach',
];

export function normalizeCategoryValue(
  value?: DiagnosedIcd['category']
): DiagnosedIcdUiCategory | null {
  if (!value) {
    return null;
  }
  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }
  const directVariant = CATEGORY_VARIANTS[stringValue as DiagnosedIcdUiCategory];
  if (directVariant) {
    return stringValue as DiagnosedIcdUiCategory;
  }
  const lookup = CATEGORY_RAW_LOOKUP[stringValue.toLowerCase()];
  return lookup ?? null;
}

export function mapCategoryToApiValue(
  value?: DiagnosedIcd['category']
): DiagnosedIcdApiCategory | null {
  const normalized = normalizeCategoryValue(value);
  return normalized ? CATEGORY_VARIANTS[normalized].apiValue : null;
}

export function resolveCategoryDisplay(
  value?: DiagnosedIcd['category']
): { code: DiagnosedIcdUiCategory; translationKey: DiagnosedIcdUiCategoryTranslationKey } | null {
  const normalized = normalizeCategoryValue(value);
  if (!normalized) {
    return null;
  }
  return {
    code: normalized,
    translationKey: CATEGORY_VARIANTS[normalized].translationKey,
  };
}

export function normalizeSicherheitValue(
  value?: DiagnosedIcd['sicherheit']
): DiagnosedIcdUiSicherheit | null {
  if (!value) {
    return null;
  }
  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }
  const directVariant = SICHERHEIT_VARIANTS[stringValue as DiagnosedIcdUiSicherheit];
  if (directVariant) {
    return stringValue as DiagnosedIcdUiSicherheit;
  }
  const lookup = SICHERHEIT_RAW_LOOKUP[stringValue.toLowerCase()];
  return lookup ?? null;
}

export function mapSicherheitToApiValue(
  value?: DiagnosedIcd['sicherheit']
): DiagnosedIcdApiSicherheit | null {
  const normalized = normalizeSicherheitValue(value);
  return normalized ? SICHERHEIT_VARIANTS[normalized].apiValue : null;
}

export function resolveSicherheitDisplay(
  value?: DiagnosedIcd['sicherheit']
): { code: 'G' | 'A' | 'V' | 'Z'; translationKey: DiagnosedIcdUiSicherheitTranslationKey } | null {
  const normalized = normalizeSicherheitValue(value);
  if (!normalized) {
    return null;
  }
  const variant = SICHERHEIT_VARIANTS[normalized];
  return {
    code: variant.code,
    translationKey: variant.translationKey,
  };
}

export type DiagnosisRelationshipBadgeVariant =
  'replaces' | 'duplicate' | 'moreGeneral' | 'similar' | 'new';

type RelationshipBadgeConfig = {
  variant: DiagnosisRelationshipBadgeVariant;
  translationKey: 'replaces' | 'alreadyExists' | 'moreGeneralThan' | 'similarTo' | 'new';
  tooltipKey?: 'replacesTooltip' | 'alreadyExistsTooltip';
};

const NEW_DIAGNOSIS_BADGE: RelationshipBadgeConfig = { variant: 'new', translationKey: 'new' };
const MORE_GENERAL_BADGE: RelationshipBadgeConfig = {
  variant: 'moreGeneral',
  translationKey: 'moreGeneralThan',
};

const REPLACES_BADGE: RelationshipBadgeConfig = {
  variant: 'replaces',
  translationKey: 'replaces',
  tooltipKey: 'replacesTooltip',
};

const RELATIONSHIP_BADGE_CONFIG: Record<DiagnosedIcdRelationship, RelationshipBadgeConfig> = {
  more_specific: REPLACES_BADGE,
  duplicate: {
    variant: 'duplicate',
    translationKey: 'alreadyExists',
    tooltipKey: 'alreadyExistsTooltip',
  },
  more_general: MORE_GENERAL_BADGE,
  similar: { variant: 'similar', translationKey: 'similarTo' },
  sibling: NEW_DIAGNOSIS_BADGE,
  unrelated: NEW_DIAGNOSIS_BADGE,
};

export function resolveDiagnosisRelationshipBadge(entry: DiagnosedIcd): {
  variant: DiagnosisRelationshipBadgeVariant;
  translationKey: RelationshipBadgeConfig['translationKey'];
  tooltipKey: RelationshipBadgeConfig['tooltipKey'];
  matchedExistingCode: string | null;
} | null {
  if (!entry.relationship) {
    return null;
  }
  const config =
    entry.relationship === 'more_specific' && !entry.matchedExistingIsDauerdiagnose
      ? NEW_DIAGNOSIS_BADGE
      : RELATIONSHIP_BADGE_CONFIG[entry.relationship];
  if (!config) {
    return null;
  }
  return {
    variant: config.variant,
    translationKey: config.translationKey,
    tooltipKey: config.tooltipKey,
    matchedExistingCode: entry.matchedExistingCode ?? null,
  };
}

export const CATEGORY_SELECT_OPTIONS = CATEGORY_ORDER.map((category) => ({
  value: CATEGORY_VARIANTS[category].apiValue,
  code: category,
  translationKey: CATEGORY_VARIANTS[category].translationKey,
}));

export const SICHERHEIT_SELECT_OPTIONS = SICHERHEIT_ORDER.map((key) => ({
  value: SICHERHEIT_VARIANTS[key].apiValue,
  code: SICHERHEIT_VARIANTS[key].code,
  uiValue: key,
  translationKey: SICHERHEIT_VARIANTS[key].translationKey,
}));

export const VALID_DIAGNOSIS_SIDES: Array<NonNullable<DiagnosedIcd['seite']>> = [
  'links',
  'rechts',
  'beidseitig',
  'keine_angabe',
];

export const normalizeKeyPart = (value?: string | null): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

export const buildDiagnosedIcdFingerprint = (entry: DiagnosedIcd): string => {
  const code = normalizeKeyPart(entry.code);
  const icdCodeValue = normalizeKeyPart(entry.icdCode?.code);
  const icdCodeTitle = normalizeKeyPart(entry.icdCode?.title);
  const category = normalizeKeyPart(entry.category ?? null);
  const sicherheit = normalizeKeyPart(entry.sicherheit ?? null);
  const seite = normalizeKeyPart(entry.seite ?? null);
  return [code, icdCodeValue, icdCodeTitle, category, sicherheit, seite].join('|');
};

export const hashString = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return hash.toString(36);
};

export const buildDiagnosedIcdSelectionKey = (entry: DiagnosedIcd): string => {
  if (entry.id) {
    return `id-${entry.id}`;
  }
  const fingerprint = buildDiagnosedIcdFingerprint(entry);
  return `fp-${hashString(fingerprint)}`;
};

export function normalizeDiagnosedIcdEntry(entry?: DiagnosedIcd | null): DiagnosedIcd {
  const safeEntry: DiagnosedIcd = {
    id: entry?.id,
    icdCode: entry?.icdCode ?? null,
    code: entry?.code ?? '',
    category: normalizeCategoryValue(entry?.category ?? null),
    sicherheit: normalizeSicherheitValue(entry?.sicherheit ?? null),
    seite: entry?.seite ?? null,
    isSelfAnamnesis: entry?.isSelfAnamnesis ?? false,
    selected: entry?.selected ?? true,
    selectable: entry?.selectable ?? true,
    relationship: entry?.relationship ?? null,
    matchedExistingCode: entry?.matchedExistingCode ?? null,
    matchedExistingIsDauerdiagnose: entry?.matchedExistingIsDauerdiagnose ?? null,
  };

  const normalizedSeite =
    safeEntry.seite && VALID_DIAGNOSIS_SIDES.includes(safeEntry.seite) ? safeEntry.seite : null;

  return {
    ...safeEntry,
    code: safeEntry.code.trim(),
    seite: normalizedSeite,
  };
}

export function sanitizeDiagnosedIcdsForApi(entries: DiagnosedIcd[]): DiagnosedIcd[] {
  const sanitizedEntries = entries.map((entry) => {
    const normalized = normalizeDiagnosedIcdEntry(entry);
    const apiCategory = mapCategoryToApiValue(normalized.category ?? null);
    const apiSicherheit = mapSicherheitToApiValue(normalized.sicherheit ?? null);
    return {
      id: normalized.id,
      icdCode: normalized.icdCode,
      code: normalized.code.trim(),
      category: apiCategory,
      sicherheit: apiSicherheit ?? null,
      seite: normalized.seite ?? null,
      isSelfAnamnesis: normalized.isSelfAnamnesis,
    };
  });

  return sanitizedEntries.filter((entry) => {
    if (entry.code.length > 0) return true;
    if (entry.category) return true;
    if (entry.sicherheit) return true;
    if (entry.seite) return true;
    return false;
  });
}
